/** Native paths are clipboard text only: never use them for server filesystem access. */
export const MAX_LOCAL_DROPBOX_ROOT_LENGTH = 4096;

type LocalPlatform = 'mac' | 'windows';

function assertPlainPath(value: string): void {
  if (/[\u0000-\u001f\u007f-\u009f]/.test(value)) {
    throw new Error('The path must not contain control characters or line breaks.');
  }
  if (value.length > MAX_LOCAL_DROPBOX_ROOT_LENGTH) {
    throw new Error(`The path must be ${MAX_LOCAL_DROPBOX_ROOT_LENGTH} characters or fewer.`);
  }
}

function assertSegments(segments: string[], platform: LocalPlatform): void {
  for (const segment of segments) {
    if (segment === '.' || segment === '..') {
      throw new Error('Use a full folder path without "." or ".." segments.');
    }
    if (platform === 'windows') {
      if (/[<>:"|?*]/.test(segment) || /[ .]$/.test(segment)) {
        throw new Error('Windows folder names cannot contain < > : " | ? * or end with a space or period.');
      }
      if (/^(?:CON|PRN|AUX|NUL|COM[1-9¹²³]|LPT[1-9¹²³])(?:\.|$)/i.test(segment)) {
        throw new Error('The path contains a reserved Windows folder name.');
      }
    }
  }
}

/** Parse one full local Projects-root path; callers use null to clear the setting. */
export function parseLocalDropboxRoot(input: string): { path: string; platform: LocalPlatform } {
  assertPlainPath(input);
  let path = input.trim();
  if (path.startsWith('"') || path.startsWith("'")) {
    const quote = path[0];
    if (path.length < 2 || !path.endsWith(quote)) {
      throw new Error('Remove unmatched quotation marks from the path.');
    }
    path = path.slice(1, -1);
  }
  if (!path) throw new Error('Paste the full local path to your shared Projects folder.');
  if (/^\\\\[?.]\\/.test(path)) {
    throw new Error('Use a normal Windows drive or network-folder path, not a device path.');
  }

  if (/^[A-Za-z]:[\\/]/.test(path)) {
    const drive = path.slice(0, 2);
    const segments = path.slice(3).split(/[\\/]+/).filter(Boolean);
    assertSegments(segments, 'windows');
    return { path: `${drive}\\${segments.join('\\')}`, platform: 'windows' };
  }
  if (/^(?:\\\\|\/\/)/.test(path)) {
    const segments = path.slice(2).split(/[\\/]+/).filter(Boolean);
    if (segments.length < 2) {
      throw new Error('A Windows network path must include both a server and a shared folder.');
    }
    assertSegments(segments, 'windows');
    return { path: `\\\\${segments.join('\\')}`, platform: 'windows' };
  }
  if (path.startsWith('/')) {
    if (path.includes('\\')) {
      throw new Error('Use forward slashes (/) throughout a Mac folder path.');
    }
    const segments = path.split(/\/+/).filter(Boolean);
    assertSegments(segments, 'mac');
    return { path: `/${segments.join('/')}`, platform: 'mac' };
  }
  throw new Error('Use an absolute Mac path (/Users/…), Windows path (C:\\…), or network path (\\\\server\\share\\…).');
}

/** Join a Dropbox-root-relative project folder to a validated native root. */
export function buildLocalProjectPath(root: string, relativePath: string): string {
  const parsed = parseLocalDropboxRoot(root);
  assertPlainPath(relativePath);
  if (!relativePath || relativePath.startsWith('/') || relativePath.includes('\\')) {
    throw new Error('The project folder must be a relative Dropbox path using forward slashes.');
  }
  const segments = relativePath.split('/');
  if (segments.some(segment => !segment)) {
    throw new Error('The project folder path contains an empty folder name.');
  }
  assertSegments(segments, parsed.platform);
  // A relative project path must never be mistaken for a URL or drive path.
  if (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(relativePath)) {
    throw new Error('The project folder must be a relative Dropbox path, not a drive path or URL.');
  }
  const separator = parsed.platform === 'windows' ? '\\' : '/';
  return `${parsed.path.replace(/[\\/]$/, '')}${separator}${segments.join(separator)}`;
}